package fr.frux.feature.userprofile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.designsystem.components.UserModerationMessage
import fr.frux.core.designsystem.components.UserReportReason
import fr.frux.shared.domain.model.SystemAccounts
import fr.frux.shared.domain.model.UserProfile
import fr.frux.shared.domain.repository.UserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class UserProfileState(
    val profile: UserProfile? = null,
    val isLoading: Boolean = true,
    /** Catégorie d'échec (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
    /** Signaler, masquer ou réafficher (ADR 0029) : lecteur connecté, autre compte, hors import. */
    val canModerate: Boolean = false,
    val isModerationPending: Boolean = false,
    /** Retour d'un geste de modération (#515), à afficher en snackbar puis consommer. */
    val moderationMessage: UserModerationMessage? = null,
)

/**
 * Profil public d'un contributeur (#227) : lecture seule, atteignable sans session depuis les
 * fiches publiques (#264). Gestes de l'ADR 0029 (#515) pour un lecteur connecté.
 */
class UserProfileViewModel(
    private val userId: Long,
    private val userRepository: UserRepository,
    /** Provider lu à chaque chargement (#264) : la session peut changer pendant la vie du VM. */
    private val currentUserIdProvider: () -> Long? = { null },
) : ViewModel() {
    private val _state = MutableStateFlow(UserProfileState())
    val state: StateFlow<UserProfileState> = _state.asStateFlow()

    init {
        load()
    }

    fun retry() = load()

    private fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            userRepository.getUserProfile(userId).fold(
                onSuccess = { profile ->
                    val viewer = currentUserIdProvider()
                    val canModerate =
                        viewer != null && viewer != profile.id && !SystemAccounts.isImport(profile.username)
                    _state.update {
                        it.copy(
                            profile = profile,
                            isLoading = false,
                            error = null,
                            canModerate = canModerate,
                        )
                    }
                },
                onFailure = { error ->
                    _state.update { it.copy(isLoading = false, error = error.asAppError()) }
                },
            )
        }
    }

    fun onReportUser(
        reason: UserReportReason,
        details: String?,
    ) {
        if (!_state.value.canModerate) return
        runModeration(UserModerationMessage.UserReported) { userRepository.reportUser(userId, reason.name, details) }
    }

    fun onBlockConfirmed() {
        val profile = _state.value.profile ?: return
        if (!_state.value.canModerate) return
        runModeration(UserModerationMessage.Blocked(profile.username), blockedAfter = true) {
            userRepository.blockUser(userId)
        }
    }

    fun onUnblock() {
        val profile = _state.value.profile ?: return
        if (!_state.value.canModerate) return
        runModeration(UserModerationMessage.Unblocked(profile.username), blockedAfter = false) {
            userRepository.unblockUser(userId)
        }
    }

    fun consumeModerationMessage() {
        _state.update { it.copy(moderationMessage = null) }
    }

    private fun runModeration(
        onSuccess: UserModerationMessage,
        blockedAfter: Boolean? = null,
        call: suspend () -> Result<Unit>,
    ) {
        if (_state.value.isModerationPending) return
        _state.update { it.copy(isModerationPending = true, moderationMessage = null) }
        viewModelScope.launch {
            call().fold(
                onSuccess = {
                    _state.update { s ->
                        s.copy(
                            isModerationPending = false,
                            moderationMessage = onSuccess,
                            profile = blockedAfter?.let { b -> s.profile?.copy(blockedByMe = b) } ?: s.profile,
                        )
                    }
                },
                onFailure = { error ->
                    _state.update {
                        it.copy(
                            isModerationPending = false,
                            moderationMessage = UserModerationMessage.Failed(error.asAppError()),
                        )
                    }
                },
            )
        }
    }
}

/** Contrat de fabrique du [UserProfileViewModel], honoré par le composition root (#43). */
fun interface UserProfileViewModelFactory {
    fun create(userId: Long): UserProfileViewModel
}

/** Fabrique installée par le composition root ; sans défaut, pour que l'oubli de câblage crie. */
val LocalUserProfileViewModelFactory =
    staticCompositionLocalOf<UserProfileViewModelFactory> {
        error("UserProfileViewModelFactory absente : le composition root doit l'installer (#43)")
    }

/** Fabrique le UserProfileViewModel depuis la fabrique fournie par le root. */
@Composable
fun rememberUserProfileViewModel(userId: Long): UserProfileViewModel {
    val factory = LocalUserProfileViewModelFactory.current
    return viewModel(key = "user-profile-$userId") { factory.create(userId) }
}
